// ============================================
// FILE: src/components/modals/CheckoutModal.jsx
// ============================================
//
// Marketplace checkout: BAG (the cart page) → ADDRESS → PAYMENT.
//
// Splitting the old single-page form into two steps is presentational only —
// every prop, gateway gate and total is unchanged. In particular:
//   - payment methods are still gated by appSettings.codEnabled /
//     payuEnabled / razorpayEnabled, with the same fallback effect
//   - the wallet still auto-enables when it covers the order, and
//     onWalletUsed still fires only for partial use
//   - processPayment({ walletAmountToUse }) is called with the same shape

import React, { useState, useEffect } from 'react';
import {
  X, ArrowLeft, Check, Plus, MapPin, Clock, Tag, Wallet,
  Banknote, QrCode, Smartphone, CreditCard, Zap, ChevronRight, ShieldCheck,
} from 'lucide-react';
import { resolveProductImageUrl } from '../../utils/imageHelpers';
import { formatCouponDiscount } from '../../utils/couponHelpers';
import { getFinalPrice, hasDiscount as isDiscounted } from '../../utils/priceHelpers';

const STEPS = [
  { key: 'bag', label: 'Bag' },
  { key: 'address', label: 'Address' },
  { key: 'payment', label: 'Payment' },
];

const CheckoutModal = ({
  cart,
  getCartTotal,
  deliverySlots,
  selectedSlot,
  setSelectedSlot,
  paymentMethod,
  setPaymentMethod,
  onClose,
  onModifyCart,
  processPayment,
  deliveryAddresses,
  setShowAddAddress,
  handleSetDefaultAddress,
  selectedAddressId,
  setSelectedAddressId,
  appliedCoupon,
  couponDiscount,
  couponCode = '',
  setCouponCode,
  couponError = '',
  handleApplyCoupon,
  handleRemoveCoupon,
  getSuggestedCoupons,
  getFinalTotal,
  getHandlingCharge,
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50, razorpayEnabled: false, payuEnabled: false, codEnabled: false },
  walletBalance = 0,
  onWalletUsed,
  upiQrCode = '/static/upi-qr.jpeg',
  upiId = '',
  isDesktop = false,
}) => {
  const [step, setStep] = useState('address');
  const [useWallet, setUseWallet] = useState(false);
  const [walletAmountInput, setWalletAmountInput] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);

  const orderTotal = getFinalTotal ? getFinalTotal() : getCartTotal();
  const maxWalletUse = Math.min(walletBalance, orderTotal);
  const walletAmountToUse = useWallet ? Math.min(parseFloat(walletAmountInput) || maxWalletUse, maxWalletUse) : 0;
  const amountDue = Math.max(0, orderTotal - walletAmountToUse);

  // Auto-enable wallet when it fully covers the order
  useEffect(() => {
    if (walletBalance >= orderTotal && orderTotal > 0) {
      setUseWallet(true);
      setWalletAmountInput(String(orderTotal));
    }
  }, [walletBalance, orderTotal]);

  // Auto-reset payment method when its gateway gets disabled
  useEffect(() => {
    const isPayuMethod     = ['upi', 'card', 'wallet', 'netbanking'].includes(paymentMethod);
    const isRazorpayMethod = paymentMethod === 'razorpay';
    const isCodFamily      = paymentMethod === 'cod' || paymentMethod === 'upi_cod';

    if (isRazorpayMethod && !appSettings.razorpayEnabled) {
      if (appSettings.payuEnabled !== false) setPaymentMethod('upi');
      else if (appSettings.codEnabled !== false) setPaymentMethod('cod');
    } else if (isPayuMethod && !appSettings.payuEnabled) {
      if (appSettings.razorpayEnabled) setPaymentMethod('razorpay');
      else if (appSettings.codEnabled !== false) setPaymentMethod('cod');
    } else if (isCodFamily && appSettings.codEnabled === false) {
      if (appSettings.razorpayEnabled) setPaymentMethod('razorpay');
      else if (appSettings.payuEnabled !== false) setPaymentMethod('upi');
    }
  }, [appSettings.razorpayEnabled, appSettings.payuEnabled, appSettings.codEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleWallet = (checked) => {
    setUseWallet(checked);
    setWalletAmountInput(checked ? String(maxWalletUse) : '');
  };

  const validAddresses = Array.isArray(deliveryAddresses)
    ? deliveryAddresses.filter((a) => a.address && a.address.trim())
    : [];
  const hasAddress = !!selectedAddressId && validAddresses.some((a) => a.id === selectedAddressId);
  const selectedAddress = validAddresses.find((a) => a.id === selectedAddressId);

  const noPaymentMethods =
    appSettings.codEnabled === false && appSettings.payuEnabled === false && !appSettings.razorpayEnabled;

  const handleConfirmPay = () => {
    if (!hasAddress) {
      alert('Please add and select a delivery address before placing your order.');
      return;
    }
    processPayment({ walletAmountToUse });
    // Only optimistically deduct for partial wallet use; full-wallet orders are
    // handled inside processPayment.
    if (walletAmountToUse > 0 && walletAmountToUse < orderTotal && onWalletUsed) {
      onWalletUsed(walletAmountToUse);
    }
  };

  // ---- Price breakdown -------------------------------------------------- //
  const subtotal = Number(getCartTotal()) || 0;
  const totalMrp = (cart || []).reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0);
  const mrpDiscount = (cart || []).reduce(
    (sum, item) => (isDiscounted(item) ? sum + (Number(item.price) - getFinalPrice(item)) * item.quantity : sum),
    0
  );
  const handlingCharge = typeof getHandlingCharge === 'function' ? Number(getHandlingCharge()) || 0 : 0;
  const qualifiesFreeShipping = subtotal >= appSettings.freeShippingThreshold;
  const tax = subtotal * ((Number(appSettings.taxRate) || 0) / 100);
  const remainingForFreeShipping = appSettings.freeShippingThreshold - (subtotal - (couponDiscount || 0));

  const sectionCard = (children, extra = '') => (
    <div className={`bg-white p-4 ${extra}`} style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
      {children}
    </div>
  );

  // ---- Step 1: Address -------------------------------------------------- //
  const addressStep = (
    <div className="space-y-3">
      {sectionCard(
        <>
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} style={{ color: 'var(--fx-ink-2)' }} />
            <h3 className="fx-label">Select Delivery Address</h3>
          </div>

          {validAddresses.length > 0 ? (
            <div className="space-y-2.5">
              {validAddresses.map((addr) => {
                const isSelected = selectedAddressId === addr.id;
                return (
                  <div
                    key={addr.id}
                    onClick={() => setSelectedAddressId && setSelectedAddressId(addr.id)}
                    className="p-3 cursor-pointer"
                    style={{
                      border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
                      borderRadius: 4,
                      background: isSelected ? '#fff8fa' : '#fff',
                    }}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          width: 16, height: 16,
                          border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-ink-3)'}`,
                        }}
                      >
                        {isSelected && (
                          <span className="rounded-full" style={{ width: 8, height: 8, background: 'var(--fx-pink)' }} />
                        )}
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span style={{ fontSize: 14, fontWeight: 700 }}>{addr.recipient_name}</span>
                          <span
                            className="px-1.5 py-0.5"
                            style={{ background: '#f5f5f6', borderRadius: 2, fontSize: 11, fontWeight: 700, color: 'var(--fx-ink-2)' }}
                          >
                            {addr.label?.toUpperCase() || 'HOME'}
                          </span>
                          {addr.isDefault && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--fx-green)' }}>DEFAULT</span>
                          )}
                        </div>
                        <p className="mt-1" style={{ fontSize: 13, color: 'var(--fx-ink-2)', lineHeight: 1.5 }}>
                          {addr.address}, {addr.city} - <span style={{ fontWeight: 700 }}>{addr.pincode}</span>
                        </p>
                        <p className="mt-1" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                          Mobile: <span style={{ fontWeight: 700 }}>{addr.phone}</span>
                        </p>

                        {isSelected && !addr.isDefault && handleSetDefaultAddress && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSetDefaultAddress(addr.id); }}
                            className="mt-2"
                            style={{ fontSize: 12, fontWeight: 700, color: 'var(--fx-pink)' }}
                          >
                            SET AS DEFAULT
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className="p-4 text-center"
              style={{ border: '1px dashed var(--fx-line)', borderRadius: 4 }}
            >
              <p style={{ fontSize: 14, fontWeight: 700 }}>No address added yet</p>
              <p className="mt-1" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                Add a delivery address to continue
              </p>
            </div>
          )}

          <button
            onClick={() => setShowAddAddress && setShowAddAddress(true)}
            className="w-full mt-3 py-2.5 flex items-center justify-center gap-1.5"
            style={{ border: '1px solid var(--fx-pink)', borderRadius: 4, color: 'var(--fx-pink)', fontSize: 13, fontWeight: 700 }}
          >
            <Plus size={15} />
            ADD NEW ADDRESS
          </button>
        </>
      )}

      {Array.isArray(deliverySlots) && deliverySlots.length > 0 && sectionCard(
        <>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} style={{ color: 'var(--fx-ink-2)' }} />
            <h3 className="fx-label">Delivery Slot</h3>
          </div>
          <div className="space-y-2">
            {deliverySlots.map((slot) => {
              const isSelected = selectedSlot === slot.id;
              return (
                <button
                  key={slot.id}
                  onClick={() => setSelectedSlot(slot.id)}
                  className="w-full flex items-center gap-2.5 p-3 text-left"
                  style={{
                    border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
                    borderRadius: 4,
                    background: isSelected ? '#fff8fa' : '#fff',
                  }}
                >
                  <span
                    className="flex-shrink-0 rounded-full flex items-center justify-center"
                    style={{
                      width: 16, height: 16,
                      border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-ink-3)'}`,
                    }}
                  >
                    {isSelected && (
                      <span className="rounded-full" style={{ width: 8, height: 8, background: 'var(--fx-pink)' }} />
                    )}
                  </span>
                  <span className="flex-1">
                    <span className="block" style={{ fontSize: 14, fontWeight: 700 }}>{slot.label}</span>
                    <span className="block" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>{slot.time}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {sectionCard(
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="fx-label">Order Summary ({(cart || []).length} items)</h3>
            {onModifyCart && (
              <button onClick={onModifyCart} style={{ fontSize: 12, fontWeight: 700, color: 'var(--fx-pink)' }}>
                EDIT
              </button>
            )}
          </div>
          <div className="space-y-3">
            {(cart || []).map((item, index) => {
              const image = resolveProductImageUrl(
                item.featured_image_url || item.image_url || item.featured_image || item.image
              );
              const lineTotal = getFinalPrice(item) * Number(item.quantity || 0);
              return (
                <div key={`${item.id}-${index}`} className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 bg-[#f5f5f6] overflow-hidden"
                    style={{ width: 44, height: 56, borderRadius: 2 }}
                  >
                    {image ? (
                      <img src={image} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">{item.icon || '🛍️'}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{item.name}</p>
                    <p style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                      {item.variant_name ? `${item.variant_name} · ` : ''}Qty {item.quantity}
                    </p>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>₹{lineTotal.toFixed(0)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  // ---- Step 2: Payment -------------------------------------------------- //
  const paymentOption = ({ id, Icon, title, subtitle, badge }) => {
    const isSelected = paymentMethod === id;
    return (
      <button
        key={id}
        onClick={() => setPaymentMethod(id)}
        className="w-full flex items-center gap-3 p-3.5 text-left"
        style={{
          border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
          borderRadius: 4,
          background: isSelected ? '#fff8fa' : '#fff',
        }}
      >
        <span
          className="flex-shrink-0 rounded-full flex items-center justify-center"
          style={{ width: 16, height: 16, border: `1px solid ${isSelected ? 'var(--fx-pink)' : 'var(--fx-ink-3)'}` }}
        >
          {isSelected && <span className="rounded-full" style={{ width: 8, height: 8, background: 'var(--fx-pink)' }} />}
        </span>

        <Icon size={19} style={{ color: isSelected ? 'var(--fx-pink)' : 'var(--fx-ink-2)', flexShrink: 0 }} />

        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span style={{ fontSize: 14, fontWeight: 700 }}>{title}</span>
            {badge && (
              <span
                className="px-1.5 py-0.5"
                style={{ background: '#e8f7f3', color: 'var(--fx-green)', borderRadius: 2, fontSize: 10, fontWeight: 700 }}
              >
                {badge}
              </span>
            )}
          </span>
          <span className="block truncate" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>{subtitle}</span>
        </span>
      </button>
    );
  };

  const paymentStep = (
    <div className="space-y-3">
      {/* Deliver-to strip, so the address stays visible while paying */}
      {selectedAddress && sectionCard(
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p style={{ fontSize: 12, color: 'var(--fx-ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
              Deliver to
            </p>
            <p className="mt-1 truncate" style={{ fontSize: 14, fontWeight: 700 }}>
              {selectedAddress.recipient_name}, {selectedAddress.pincode}
            </p>
            <p className="truncate" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
              {selectedAddress.address}, {selectedAddress.city}
            </p>
          </div>
          <button
            onClick={() => setStep('address')}
            className="flex-shrink-0 px-3 py-1.5"
            style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 12, fontWeight: 700 }}
          >
            CHANGE
          </button>
        </div>
      )}

      {/* Wallet */}
      {walletBalance > 0 && sectionCard(
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Wallet size={18} style={{ color: 'var(--fx-ink-2)' }} />
              <div>
                <p style={{ fontSize: 14, fontWeight: 700 }}>Use Wallet Balance</p>
                <p style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                  Available ₹{walletBalance.toFixed(2)}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={useWallet}
                onChange={(e) => handleToggleWallet(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#ff3f6c] peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          </div>

          {useWallet && (
            <div className="mt-3 flex items-center gap-2">
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>₹</span>
              <input
                type="number"
                min="1"
                max={maxWalletUse}
                value={walletAmountInput}
                onChange={(e) => setWalletAmountInput(e.target.value)}
                className="flex-1 px-3 py-2 outline-none"
                style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 14 }}
              />
              <button
                onClick={() => setWalletAmountInput(String(maxWalletUse))}
                className="px-3 py-2"
                style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 12, fontWeight: 700 }}
              >
                MAX
              </button>
            </div>
          )}

          {walletAmountToUse > 0 && (
            <p className="mt-2 flex items-center gap-1" style={{ fontSize: 12, color: 'var(--fx-green)', fontWeight: 700 }}>
              <Check size={13} />
              ₹{walletAmountToUse.toFixed(2)} will be deducted from your wallet
            </p>
          )}
        </>
      )}

      {/* Payment methods, or the wallet-covers-everything state */}
      {amountDue <= 0 ? (
        sectionCard(
          <div className="flex items-center gap-3">
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--fx-green)' }}
            >
              <Check size={17} className="text-white" />
            </span>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700 }}>Paying with Wallet</p>
              <p style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>No additional payment required</p>
            </div>
          </div>
        )
      ) : (
        sectionCard(
          <>
            <h3 className="fx-label mb-3">Choose Payment Mode</h3>

            {noPaymentMethods ? (
              <div className="p-3 text-center" style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
                <p style={{ fontSize: 14, fontWeight: 700 }}>No payment methods available</p>
                <p className="mt-1" style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                  Please contact support or try again later.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {appSettings.payuEnabled !== false && (
                  <>
                    {paymentOption({ id: 'upi', Icon: Smartphone, title: 'UPI', subtitle: 'PhonePe, Google Pay, Paytm' })}
                    {paymentOption({ id: 'card', Icon: CreditCard, title: 'Credit / Debit Card', subtitle: 'Visa, Mastercard, RuPay' })}
                    {paymentOption({ id: 'wallet', Icon: Wallet, title: 'Digital Wallet', subtitle: 'Paytm, Amazon Pay' })}
                  </>
                )}

                {appSettings.razorpayEnabled &&
                  paymentOption({
                    id: 'razorpay', Icon: Zap, title: 'Razorpay',
                    subtitle: 'UPI · Card · Net Banking · Wallet', badge: 'SECURE',
                  })}

                {appSettings.codEnabled !== false && (
                  <>
                    {paymentOption({ id: 'cod', Icon: Banknote, title: 'Cash on Delivery', subtitle: 'Pay when you receive' })}
                    {paymentOption({ id: 'upi_cod', Icon: QrCode, title: 'UPI QR at Delivery', subtitle: 'Scan the QR when delivered' })}

                    {paymentMethod === 'upi_cod' && (
                      <div
                        className="flex flex-col items-center p-4"
                        style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}
                      >
                        <p
                          className="mb-3"
                          style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fx-ink-2)' }}
                        >
                          QR shown at delivery
                        </p>
                        <img
                          src={upiQrCode}
                          alt="UPI QR Code"
                          style={{ width: 150, height: 150, borderRadius: 4, display: 'block' }}
                        />
                        {upiId && (
                          <p className="mt-2" style={{ fontSize: 13, fontWeight: 700 }}>{upiId}</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )
      )}
    </div>
  );

  // ---- Coupons ---------------------------------------------------------- //
  const couponBlock = sectionCard(
    <>
      <h3 className="fx-label mb-3">Coupons</h3>

      {appliedCoupon ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--fx-green)' }}
            >
              <Check size={14} className="text-white" />
            </span>
            <div className="min-w-0">
              <p className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{appliedCoupon.code}</p>
              <p style={{ fontSize: 13, color: 'var(--fx-green)' }}>You saved ₹{couponDiscount}</p>
            </div>
          </div>
          <button onClick={handleRemoveCoupon} style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-pink)' }}>
            REMOVE
          </button>
        </div>
      ) : (
        <>
          <button
            onClick={() => setShowCouponInput(!showCouponInput)}
            className="w-full flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2.5">
              <Tag size={16} style={{ color: 'var(--fx-ink-2)' }} />
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Apply Coupons</span>
            </span>
            <ChevronRight size={16} style={{ color: 'var(--fx-ink-3)' }} />
          </button>

          {showCouponInput && (
            <div className="mt-4 space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode && setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon && handleApplyCoupon()}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2 outline-none uppercase placeholder:normal-case"
                  style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 14 }}
                />
                <button
                  onClick={() => handleApplyCoupon && handleApplyCoupon()}
                  className="px-5 text-white"
                  style={{ background: 'var(--fx-pink)', borderRadius: 2, fontSize: 13, fontWeight: 700 }}
                >
                  APPLY
                </button>
              </div>

              {couponError && (
                <p className="flex items-center gap-1" style={{ fontSize: 13, color: 'var(--fx-pink)' }}>
                  <X size={14} />{couponError}
                </p>
              )}

              {getSuggestedCoupons && (() => {
                const suggested = getSuggestedCoupons();
                if (!suggested || suggested.length === 0) return null;

                return (
                  <div className="space-y-2 pt-1">
                    {suggested.map((coupon) => {
                      const eligible = getCartTotal() >= coupon.minOrder;
                      const shortfall = coupon.minOrder - getCartTotal();
                      return (
                        <div
                          key={coupon.code}
                          className="p-3 flex items-center justify-between gap-3"
                          style={{
                            border: '1px dashed var(--fx-line)',
                            borderRadius: 2,
                            opacity: eligible ? 1 : 0.55,
                          }}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span style={{ fontSize: 14, fontWeight: 700 }}>{coupon.code}</span>
                              <span
                                className="px-1.5 py-0.5"
                                style={{ background: '#e8f7f3', color: 'var(--fx-green)', borderRadius: 2, fontSize: 11, fontWeight: 700 }}
                              >
                                {formatCouponDiscount(coupon)}
                              </span>
                            </div>
                            <p className="truncate" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
                              {eligible ? coupon.description : `Add ₹${shortfall.toFixed(0)} to unlock`}
                            </p>
                          </div>
                          <button
                            disabled={!eligible}
                            onClick={() => handleApplyCoupon && handleApplyCoupon(coupon.code)}
                            style={{
                              fontSize: 12, fontWeight: 700,
                              color: eligible ? 'var(--fx-pink)' : 'var(--fx-ink-3)',
                              cursor: eligible ? 'pointer' : 'not-allowed',
                            }}
                          >
                            {eligible ? 'APPLY' : 'LOCKED'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}
    </>
  );

  // ---- Price panel ------------------------------------------------------ //
  const priceRow = (label, value, { color, bold } = {}) => (
    <div className="flex items-center justify-between py-1.5">
      <span style={{ fontSize: 14, color: bold ? 'var(--fx-ink)' : 'var(--fx-ink-2)', fontWeight: bold ? 700 : 400 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: bold ? 700 : 400, color: color || 'var(--fx-ink)' }}>{value}</span>
    </div>
  );

  const priceDetails = sectionCard(
    <>
      <h3 className="fx-label mb-2">Price Details ({(cart || []).length} Items)</h3>

      {priceRow('Total MRP', `₹${totalMrp.toFixed(0)}`)}
      {mrpDiscount > 0 && priceRow('Discount on MRP', `-₹${mrpDiscount.toFixed(0)}`, { color: 'var(--fx-green)' })}
      {appliedCoupon && couponDiscount > 0 &&
        priceRow(`Coupon (${appliedCoupon.code})`, `-₹${couponDiscount}`, { color: 'var(--fx-green)' })}
      {handlingCharge > 0 && priceRow('Platform Fee', `₹${handlingCharge.toFixed(0)}`)}
      {tax > 0 && priceRow(`GST (${appSettings.taxRate}%)`, `₹${tax.toFixed(0)}`)}

      <div className="flex items-center justify-between py-1.5">
        <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>Shipping Fee</span>
        <span style={{ fontSize: 14 }}>
          {qualifiesFreeShipping ? (
            <>
              <span style={{ color: 'var(--fx-ink-3)', textDecoration: 'line-through', marginRight: 6 }}>
                ₹{appSettings.standardShippingFee}
              </span>
              <span style={{ color: 'var(--fx-green)', fontWeight: 700 }}>FREE</span>
            </>
          ) : (
            `₹${appSettings.standardShippingFee}`
          )}
        </span>
      </div>

      {walletAmountToUse > 0 &&
        priceRow('Wallet Deduction', `-₹${walletAmountToUse.toFixed(2)}`, { color: 'var(--fx-green)' })}

      <div className="mt-2 pt-3" style={{ borderTop: '1px solid var(--fx-line)' }}>
        {priceRow(walletAmountToUse > 0 ? 'Amount Due' : 'Total Amount', `₹${amountDue.toFixed(0)}`, { bold: true })}
      </div>

      {!qualifiesFreeShipping && remainingForFreeShipping > 0 && (
        <p className="mt-3" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
          Add <span style={{ fontWeight: 700, color: 'var(--fx-pink)' }}>₹{remainingForFreeShipping.toFixed(0)}</span> more for FREE delivery
        </p>
      )}

      {isDesktop && (
        <button
          onClick={step === 'address' ? () => setStep('payment') : handleConfirmPay}
          disabled={!hasAddress || (step === 'payment' && noPaymentMethods && amountDue > 0)}
          className="fx-btn fx-btn-primary w-full mt-4"
        >
          {step === 'address'
            ? 'Continue'
            : amountDue <= 0 ? 'Place Order' : `Pay ₹${amountDue.toFixed(0)}`}
        </button>
      )}

      {!hasAddress && (
        <p className="mt-2 text-center" style={{ fontSize: 12, color: 'var(--fx-pink)' }}>
          Select a delivery address to continue
        </p>
      )}
    </>
  );

  // ---- Shell ------------------------------------------------------------ //
  return (
    // A flex column that fills the viewport: header and action bar are fixed
    // panes, and only the middle scrolls. Previously the whole overlay
    // scrolled, which stacked its scrollbar on top of the page's own.
    <div className="fashion-scope checkout-overlay fixed inset-0 z-[300] bg-[#fafafa] flex flex-col overflow-hidden">
      {/* Header + steps */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid var(--fx-line)' }}>
        <div className="flex items-center gap-3 px-4 py-3 w-full max-w-[1200px] mx-auto">
          <button
            onClick={() => (step === 'payment' ? setStep('address') : onClose())}
            className="p-1 -ml-1"
            aria-label="Go back"
          >
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label flex-1">Checkout</span>
          <span className="flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--fx-ink-2)' }}>
            <ShieldCheck size={14} style={{ color: 'var(--fx-green)' }} />
            100% Secure
          </span>
          <button onClick={onClose} className="p-1 -mr-1" aria-label="Close checkout">
            <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>

        <div
          className="flex items-center justify-center gap-2 py-3 w-full max-w-[1200px] mx-auto"
          style={{ borderTop: '1px solid var(--fx-line)' }}
        >
          {STEPS.map((s, index) => {
            // Bag is behind us; address/payment track the wizard position.
            const isActive = s.key === step;
            const isDone = s.key === 'bag' || (s.key === 'address' && step === 'payment');
            return (
              <React.Fragment key={s.key}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: isActive ? 'var(--fx-pink)' : isDone ? 'var(--fx-ink)' : 'var(--fx-ink-3)',
                  }}
                >
                  {s.label}
                </span>
                {index < STEPS.length - 1 && (
                  <span style={{ width: 28, height: 1, background: 'var(--fx-line)' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Body — the only scrolling region. The slim scrollbar is deliberate:
          it is the only cue that the order summary continues below the form,
          and hiding it entirely meant shoppers never scrolled to it. */}
      <div className="flex-1 min-h-0 overflow-y-auto fx-scroll">
        <div className="w-full max-w-[1200px] mx-auto px-3 sm:px-4 py-4 lg:flex lg:gap-6 lg:items-start">
          <div className="lg:flex-1 lg:min-w-0">
            {step === 'address' ? addressStep : paymentStep}

            {/* Below lg the summary follows the form instead of sitting in a
                side rail — same markup, so nothing renders twice. */}
            <div className="mt-3 space-y-3 lg:hidden">
              {couponBlock}
              {priceDetails}
            </div>
          </div>

          <div className="hidden lg:block lg:w-[340px] lg:flex-shrink-0 space-y-3 lg:sticky lg:top-4">
            {couponBlock}
            {priceDetails}
          </div>
        </div>
      </div>

      {/* Action bar — a flex pane rather than a fixed overlay, so it can never
          sit on top of the last field. Hidden at lg, where the rail owns the CTA. */}
      {!isDesktop && (
        <div
          className="flex-shrink-0 bg-white flex items-center gap-3 px-3 py-2.5 lg:hidden"
          style={{ borderTop: '1px solid var(--fx-line)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          <div className="flex-shrink-0">
            <p style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>₹{amountDue.toFixed(0)}</p>
            <button onClick={onModifyCart} style={{ fontSize: 11, color: 'var(--fx-pink)', fontWeight: 700 }}>
              VIEW DETAILS
            </button>
          </div>
          <button
            onClick={step === 'address' ? () => setStep('payment') : handleConfirmPay}
            disabled={!hasAddress || (step === 'payment' && noPaymentMethods && amountDue > 0)}
            className="fx-btn fx-btn-primary flex-1"
          >
            {step === 'address'
              ? 'Continue'
              : amountDue <= 0 ? 'Place Order' : `Pay ₹${amountDue.toFixed(0)}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default CheckoutModal;
